import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import SidebarLayout from "./SidebarLayout";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const Analytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("services"); // 'services', 'products', or 'payouts'
  const [dateRange, setDateRange] = useState("30days"); // '7days', '30days', '90days', 'year', 'all'
  const [selectedLocation, setSelectedLocation] = useState("all");

  // Data states
  const [bookings, setBookings] = useState([]);
  const [locations, setLocations] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [productSales, setProductSales] = useState([]);

  // Product analytics state (matching ProductAnalytics.js)
  const [salesData, setSalesData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);
  const [categoryData, setCategoryData] = useState(null);

  // Payout analytics state
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [canonicalReconciliation, setCanonicalReconciliation] = useState(null);
  const [payoutExpandedSections, setPayoutExpandedSections] = useState({
    held: true,
    released: true,
    history: false,
  });

  // Product analytics helper functions
  const getProductDateRange = () => {
    const end = dayjs();
    let start;

    switch (dateRange) {
      case "currentMonth":
        start = dayjs().startOf("month");
        break;
      case "currentYear":
        start = dayjs().startOf("year");
        break;
      case "7days":
        start = dayjs().subtract(7, "days");
        break;
      case "30days":
        start = dayjs().subtract(30, "days");
        break;
      case "90days":
        start = dayjs().subtract(90, "days");
        break;
      case "year":
        start = dayjs().subtract(1, "year");
        break;
      case "all":
        start = dayjs().subtract(10, "years");
        break;
      default:
        start = dayjs().subtract(30, "days");
    }

    return {
      start: start.format("YYYY-MM-DD"),
      end: end.format("YYYY-MM-DD"),
    };
  };

  const topProductsChartData = () => {
    if (!salesData?.sales || salesData.sales.length === 0) return null;
    const top10 = salesData.sales.slice(0, 10);
    return {
      labels: top10.map((p) => p.name || "Unknown"),
      datasets: [
        {
          label: "Revenue ($)",
          data: top10.map((p) => p.revenue || 0),
          backgroundColor: "rgba(59, 130, 246, 0.8)",
          borderColor: "rgb(59, 130, 246)",
          borderWidth: 1,
        },
      ],
    };
  };

  const categoryRevenueChartData = () => {
    if (!categoryData?.categories || categoryData.categories.length === 0)
      return null;
    const colors = [
      "rgba(59, 130, 246, 0.8)",
      "rgba(16, 185, 129, 0.8)",
      "rgba(245, 158, 11, 0.8)",
      "rgba(239, 68, 68, 0.8)",
      "rgba(139, 92, 246, 0.8)",
      "rgba(236, 72, 153, 0.8)",
    ];
    return {
      labels: categoryData.categories.map((c) => c.category),
      datasets: [
        {
          data: categoryData.categories.map((c) => c.revenue),
          backgroundColor: colors,
          borderColor: colors.map((c) => c.replace("0.8", "1")),
          borderWidth: 2,
        },
      ],
    };
  };

  const velocityChartData = () => {
    if (!performanceData?.performance) return null;
    const sorted = [...performanceData.performance]
      .sort((a, b) => parseFloat(b.salesVelocity) - parseFloat(a.salesVelocity))
      .slice(0, 10);
    return {
      labels: sorted.map((p) => p.name),
      datasets: [
        {
          label: "Sales Velocity (units/day)",
          data: sorted.map((p) => parseFloat(p.salesVelocity)),
          backgroundColor: "rgba(16, 185, 129, 0.8)",
          borderColor: "rgb(16, 185, 129)",
          borderWidth: 1,
        },
      ],
    };
  };

  const productChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: "top" },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        titleFont: { size: 14 },
        bodyFont: { size: 13 },
      },
    },
    scales: {
      y: { beginAtZero: true, grid: { color: "rgba(0, 0, 0, 0.05)" } },
      x: { grid: { display: false } },
    },
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Auto-refresh product analytics when location, date range, or tab changes
  useEffect(() => {
    if (activeTab === "products") {
      fetchProductAnalytics();
    }
  }, [selectedLocation, dateRange, activeTab]);

  // Auto-refresh payout analytics when tab or date range changes
  useEffect(() => {
    if (activeTab === "payouts") {
      fetchPayoutAnalytics();
    }
  }, [dateRange, activeTab]);

  const fetchProductAnalytics = async () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) return;

    try {
      // Get date range based on selected period
      const { start, end } = getProductDateRange();
      const shopIdParam =
        selectedLocation !== "all" ? `&shopId=${selectedLocation}` : "";
      const dateParams = `start=${start}&end=${end}`;

      const [sales, performance, inventory, categories] = await Promise.all([
        axios
          .get(
            `${API_BASE_URL}/api/analytics/product-sales?${dateParams}${shopIdParam}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          )
          .catch((err) => {
            return { data: null };
          }),
        axios
          .get(
            `${API_BASE_URL}/api/analytics/product-performance?${dateParams}${shopIdParam}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          )
          .catch((err) => {
            return { data: null };
          }),
        axios
          .get(
            `${API_BASE_URL}/api/analytics/inventory-report?${dateParams}${shopIdParam}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          )
          .catch((err) => {
            return { data: null };
          }),
        axios
          .get(
            `${API_BASE_URL}/api/analytics/category-performance?${dateParams}${shopIdParam}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          )
          .catch((err) => {
            return { data: null };
          }),
      ]);

      setSalesData(sales.data);
      setPerformanceData(performance.data);
      setInventoryReport(inventory.data);
      setCategoryData(categories.data);
    } catch (error) {
    }
  };

  const fetchPayoutAnalytics = async () => {
    setPayoutLoading(true);
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };

      const reconcRes = await axios
        .get(`${API_BASE_URL}/api/payout-ledger/business/reconciliation`, {
          headers,
        })
        .catch(() => ({ data: null }));

      setCanonicalReconciliation(reconcRes.data || null);
    } catch (error) {
      setCanonicalReconciliation(null);
    } finally {
      setPayoutLoading(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem("ogestyle_token");

    try {
      // First, get user's shops to get shopId for staff queries
      const locationsRes = await axios.get(
        `${API_BASE_URL}/api/shop/my-shops`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const userShops = Array.isArray(locationsRes.data)
        ? locationsRes.data
        : locationsRes.data.shops || [];
      setLocations(userShops);

      // Use first shop as default if no location selected
      const currentShopId =
        selectedLocation !== "all"
          ? selectedLocation
          : userShops[0]?._id || null;

      // Get business ID from first shop for business-wide queries
      const businessId =
        userShops[0]?.businessId?._id || userShops[0]?.businessId;

      // Fetch rest of the data
      const [bookingsRes, staffRes, servicesRes, productsRes] =
        await Promise.all([
          axios
            .get(`${API_BASE_URL}/api/bookings/all`, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .catch(() => ({ data: { bookings: [] } })),
          // Get ALL staff across ALL locations for this business (not just one location)
          businessId
            ? axios
                .get(`${API_BASE_URL}/api/staff/by-business/${businessId}`, {
                  headers: { Authorization: `Bearer ${token}` },
                })
                .catch(() => ({ data: { staffList: [] } }))
            : Promise.resolve({ data: { staffList: [] } }),
          // Owner-only accounts won't have staff services endpoint access
          currentShopId
            ? axios
                .get(`${API_BASE_URL}/api/services/shop/${currentShopId}`, {
                  headers: { Authorization: `Bearer ${token}` },
                })
                .catch(() => ({ data: { services: [] } }))
            : Promise.resolve({ data: { services: [] } }),
          // Products endpoint - get for current shop if available
          currentShopId
            ? axios
                .get(`${API_BASE_URL}/api/v2/products/shop/${currentShopId}`, {
                  headers: { Authorization: `Bearer ${token}` },
                })
                .catch(() => ({ data: { products: [] } }))
            : Promise.resolve({ data: { products: [] } }),
        ]);

      setBookings(bookingsRes.data.bookings || bookingsRes.data || []);

      // Extract staff from staffByLocation structure
      let allStaff = [];
      if (staffRes.data.staffByLocation) {
        Object.values(staffRes.data.staffByLocation).forEach((location) => {
          if (location.activeStaff && Array.isArray(location.activeStaff)) {
            allStaff.push(...location.activeStaff);
          }
        });
      }
      setStaff(allStaff);

      setServices(servicesRes.data.services || servicesRes.data || []);
      setProducts(productsRes.data.products || []);

      // Fetch complete product analytics with date range (industry standard: shop handles timezone conversion)
      const { start, end } = getProductDateRange(); // Get date range based on selected period
      const shopIdParam =
        selectedLocation !== "all" ? `&shopId=${selectedLocation}` : "";
      const dateParams = `start=${start}&end=${end}`; // Send local dates, backend converts using shop timezone

      const [sales, performance, inventory, categories] = await Promise.all([
        axios
          .get(
            `${API_BASE_URL}/api/analytics/product-sales?${dateParams}${shopIdParam}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          )
          .catch((err) => {
            return { data: null };
          }),
        axios
          .get(
            `${API_BASE_URL}/api/analytics/product-performance?${dateParams}${shopIdParam}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          )
          .catch((err) => {
            return { data: null };
          }),
        axios
          .get(
            `${API_BASE_URL}/api/analytics/inventory-report?${dateParams}${shopIdParam}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          )
          .catch((err) => {
            return { data: null };
          }),
        axios
          .get(
            `${API_BASE_URL}/api/analytics/category-performance?${dateParams}${shopIdParam}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          )
          .catch((err) => {
            return { data: null };
          }),
      ]);
      setSalesData(sales.data);
      setPerformanceData(performance.data);
      setInventoryReport(inventory.data);
      setCategoryData(categories.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Filter data by date range and location
  const getFilteredData = () => {
    const now = dayjs();
    let startDate;

    switch (dateRange) {
      case "7days":
        startDate = now.subtract(7, "day");
        break;
      case "30days":
        startDate = now.subtract(30, "day");
        break;
      case "90days":
        startDate = now.subtract(90, "day");
        break;
      case "year":
        startDate = now.subtract(1, "year");
        break;
      case "all":
      default:
        startDate = dayjs("2000-01-01");
        break;
    }

    return bookings.filter((booking) => {
      // Exclude imported/historical bookings paid outside the platform
      if (
        booking.isImported === true ||
        booking.paidInExternalSystem === true ||
        booking.bookingType === "manual" ||
        booking.importedAt
      ) {
        return false;
      }
      const bookingDate = dayjs(booking.appointmentDate);
      const isInDateRange = bookingDate.isAfter(startDate);
      const isInLocation =
        selectedLocation === "all" ||
        booking.shopId?._id === selectedLocation ||
        booking.shopId === selectedLocation;
      return isInDateRange && isInLocation;
    });
  };

  // Calculate key metrics
  const calculateMetrics = () => {
    const filtered = getFilteredData();
    const completed = filtered.filter((b) => b.status === "completed");

    const totalRevenue = completed.reduce((sum, b) => {
      return sum + (b.servicePriceAtBooking || 0) + (b.tipAmount || 0);
    }, 0);

    // Calculate business revenue (what business keeps after professional commission)
    const businessRevenue = completed.reduce((sum, b) => {
      // If paymentSplit exists (singular, not plural!), use businessEarnings
      if (b.paymentSplit?.businessEarnings !== undefined) {
        return sum + b.paymentSplit.businessEarnings;
      }

      // Fallback: Calculate business share for bookings without paymentSplit
      const servicePrice = b.servicePriceAtBooking || 0;
      const tipAmount = b.tipAmount || 0;
      const totalBookingAmount = servicePrice + tipAmount;

      // Find the staff member for this booking to get their commission rate
      const bookingStaff = staff.find(
        (s) => s._id === b.staffId?._id || s._id === b.staffId,
      );

      if (!bookingStaff) {
        // If staff not found, can't calculate commission - assume 100% business revenue
        // (This handles edge cases like deleted staff or orphaned bookings)
        return sum + totalBookingAmount;
      }

      // Calculate based on staff commission type
      let businessShare = 0;

      if (bookingStaff.commissionType === "percentage") {
        // commissionRate is what BUSINESS keeps (e.g., 30 means business keeps 30%)
        const businessRate = bookingStaff.commissionRate || 0;
        businessShare = totalBookingAmount * (businessRate / 100);
      } else if (bookingStaff.commissionType === "flat") {
        // Business gets fixed amount
        businessShare = Math.min(
          bookingStaff.flatCommissionAmount || 0,
          totalBookingAmount,
        );
      } else if (bookingStaff.commissionType === "none") {
        // Professional is salaried, business gets full amount
        businessShare = totalBookingAmount;
      } else if (bookingStaff.role === "R4") {
        // Freelancer - business gets 0%
        businessShare = 0;
      } else {
        // Default fallback: assume 50/50 split
        businessShare = totalBookingAmount * 0.5;
      }

      return sum + businessShare;
    }, 0);

    const serviceRevenue = completed.reduce(
      (sum, b) => sum + (b.servicePriceAtBooking || 0),
      0,
    );
    const tipRevenue = completed.reduce(
      (sum, b) => sum + (b.tipAmount || 0),
      0,
    );

    const avgBookingValue =
      completed.length > 0 ? serviceRevenue / completed.length : 0;

    // Calculate growth compared to previous period
    const prevPeriodStart =
      dateRange === "7days"
        ? 14
        : dateRange === "30days"
          ? 60
          : dateRange === "90days"
            ? 180
            : 365;
    const prevFiltered = bookings
      .filter((b) => {
        const bookingDate = dayjs(b.appointmentDate);
        const periodStart = dayjs().subtract(prevPeriodStart, "day");
        const periodEnd = dayjs().subtract(prevPeriodStart / 2, "day");
        return (
          bookingDate.isAfter(periodStart) && bookingDate.isBefore(periodEnd)
        );
      })
      .filter((b) => b.status === "completed");

    const prevRevenue = prevFiltered.reduce(
      (sum, b) => sum + (b.servicePriceAtBooking || 0),
      0,
    );
    const revenueGrowth =
      prevRevenue > 0 ? ((totalRevenue - prevRevenue) / prevRevenue) * 100 : 0;

    return {
      totalBookings: filtered.length,
      completedBookings: completed.length,
      cancelledBookings: filtered.filter((b) => b.status === "cancelled")
        .length,
      noShowBookings: filtered.filter((b) => b.status === "no-show").length,
      totalRevenue,
      businessRevenue,
      serviceRevenue,
      tipRevenue,
      avgBookingValue,
      revenueGrowth,
      completionRate:
        filtered.length > 0 ? (completed.length / filtered.length) * 100 : 0,
    };
  };

  // Revenue over time chart data
  const getRevenueChartData = () => {
    const filtered = getFilteredData().filter((b) => b.status === "completed");
    const dataMap = {};

    filtered.forEach((booking) => {
      const date = dayjs(booking.appointmentDate).format("MMM D");
      const revenue =
        (booking.servicePriceAtBooking || 0) +
        (booking.productsTotalPrice || 0);
      dataMap[date] = (dataMap[date] || 0) + revenue;
    });

    const sortedDates = Object.keys(dataMap).sort((a, b) => {
      return dayjs(a, "MMM D").diff(dayjs(b, "MMM D"));
    });

    return {
      labels: sortedDates.slice(-30), // Last 30 data points
      datasets: [
        {
          label: "Daily Revenue",
          data: sortedDates.slice(-30).map((date) => dataMap[date]),
          borderColor: "#3b82f6",
          backgroundColor: "rgba(59, 130, 246, 0.1)",
          tension: 0.4,
          fill: true,
        },
      ],
    };
  };

  // Top services chart data
  const getTopServicesData = () => {
    const filtered = getFilteredData().filter((b) => b.status === "completed");
    const serviceMap = {};

    filtered.forEach((booking) => {
      const serviceName = booking.serviceId?.name || "Unknown Service";
      serviceMap[serviceName] =
        (serviceMap[serviceName] || 0) + (booking.servicePriceAtBooking || 0);
    });

    const sorted = Object.entries(serviceMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    return {
      labels: sorted.map(([name]) => name),
      datasets: [
        {
          label: "Revenue ($)",
          data: sorted.map(([, revenue]) => revenue),
          backgroundColor: [
            "#3b82f6",
            "#8b5cf6",
            "#ec4899",
            "#f59e0b",
            "#10b981",
            "#06b6d4",
            "#6366f1",
            "#f97316",
            "#84cc16",
            "#14b8a6",
          ],
        },
      ],
    };
  };

  // Staff performance data
  const getStaffPerformanceData = () => {
    const filtered = getFilteredData().filter((b) => b.status === "completed");
    const staffMap = {};

    filtered.forEach((booking) => {
      // Handle nested staff data structure (staffId.userId.firstName)
      let staffName = "Unassigned";
      if (booking.staffId) {
        if (booking.staffId.userId && booking.staffId.userId.firstName) {
          staffName =
            `${booking.staffId.userId.firstName} ${booking.staffId.userId.lastName || ""}`.trim();
        } else if (booking.staffId.firstName) {
          staffName =
            `${booking.staffId.firstName} ${booking.staffId.lastName || ""}`.trim();
        }
      }

      if (!staffMap[staffName]) {
        staffMap[staffName] = { bookings: 0, revenue: 0 };
      }

      staffMap[staffName].bookings += 1;
      staffMap[staffName].revenue +=
        (booking.servicePriceAtBooking || 0) +
        (booking.productsTotalPrice || 0);
    });

    const sorted = Object.entries(staffMap)
      .sort((a, b) => b[1].revenue - a[1].revenue)
      .slice(0, 10);

    return {
      labels: sorted.map(([name]) => name),
      datasets: [
        {
          label: "Revenue ($)",
          data: sorted.map(([, data]) => data.revenue),
          backgroundColor: "#10b981",
        },
        {
          label: "Bookings",
          data: sorted.map(([, data]) => data.bookings),
          backgroundColor: "#3b82f6",
        },
      ],
    };
  };

  // Booking status distribution
  const getStatusDistributionData = () => {
    const filtered = getFilteredData();
    const statusCounts = {
      completed: filtered.filter((b) => b.status === "completed").length,
      confirmed: filtered.filter((b) => b.status === "confirmed").length,
      pending: filtered.filter((b) => b.status === "pending").length,
      cancelled: filtered.filter((b) => b.status === "cancelled").length,
      "no-show": filtered.filter((b) => b.status === "no-show").length,
    };

    return {
      labels: ["Completed", "Confirmed", "Pending", "Cancelled", "No-Show"],
      datasets: [
        {
          data: Object.values(statusCounts),
          backgroundColor: [
            "#10b981", // completed - green
            "#3b82f6", // confirmed - blue
            "#f59e0b", // pending - yellow
            "#ef4444", // cancelled - red
            "#6b7280", // no-show - gray
          ],
        },
      ],
    };
  };

  const getBookingRevenue = (booking) => {
    return (
      Number(booking?.servicePriceAtBooking || 0) +
      Number(booking?.productsTotalPrice || 0)
    );
  };

  const getServiceCategoryLabel = (booking) => {
    const category = booking?.serviceId?.category;
    if (Array.isArray(category)) {
      return category[0] || "Uncategorized";
    }
    return category || "Uncategorized";
  };

  const getCustomerKey = (booking) => {
    const nestedId = booking?.customerId?._id;
    const plainId = booking?.customerId;
    const email = booking?.customerEmail;
    return nestedId || plainId || email || null;
  };

  const parseHourFromStartTime = (startTime) => {
    if (!startTime || typeof startTime !== "string") return null;
    const match = startTime.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*([AP]M)?$/i);
    if (!match) return null;

    let hour = Number(match[1]);
    const suffix = (match[3] || "").toUpperCase();
    if (suffix === "PM" && hour < 12) hour += 12;
    if (suffix === "AM" && hour === 12) hour = 0;
    if (hour < 0 || hour > 23) return null;
    return hour;
  };

  const getTimeBand = (hour) => {
    if (hour >= 6 && hour <= 11) return "Morning";
    if (hour >= 12 && hour <= 16) return "Afternoon";
    if (hour >= 17 && hour <= 21) return "Evening";
    return "Other";
  };

  const getServiceInsights = () => {
    const filtered = getFilteredData();
    const completed = filtered.filter((b) => b.status === "completed");
    const cancelled = filtered.filter((b) => b.status === "cancelled");

    const bookingsWithAddOn = completed.filter(
      (b) => Number(b?.productsTotalPrice || 0) > 0,
    );
    const addOnRate =
      completed.length > 0
        ? (bookingsWithAddOn.length / completed.length) * 100
        : 0;

    const byCustomer = new Map();
    const sortedCompleted = [...completed].sort(
      (a, b) =>
        new Date(a?.appointmentDate || a?.createdAt || 0) -
        new Date(b?.appointmentDate || b?.createdAt || 0),
    );

    let newCustomerRevenue = 0;
    let returningCustomerRevenue = 0;

    sortedCompleted.forEach((booking) => {
      const key = getCustomerKey(booking);
      if (!key) return;

      const revenue = getBookingRevenue(booking);
      if (!byCustomer.has(key)) {
        byCustomer.set(key, { count: 1 });
        newCustomerRevenue += revenue;
      } else {
        const prev = byCustomer.get(key);
        prev.count += 1;
        byCustomer.set(key, prev);
        returningCustomerRevenue += revenue;
      }
    });

    const totalCustomers = byCustomer.size;
    const repeatCustomers = Array.from(byCustomer.values()).filter(
      (c) => c.count > 1,
    ).length;
    const repeatRate = totalCustomers > 0 ? (repeatCustomers / totalCustomers) * 100 : 0;

    const categoryMap = {};
    completed.forEach((booking) => {
      const category = getServiceCategoryLabel(booking);
      if (!categoryMap[category]) {
        categoryMap[category] = { category, revenue: 0, bookings: 0 };
      }
      categoryMap[category].revenue += Number(booking?.servicePriceAtBooking || 0);
      categoryMap[category].bookings += 1;
    });

    const topServiceCategories = Object.values(categoryMap)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);

    const dayMap = {};
    const bandMap = {};
    completed.forEach((booking) => {
      const day = dayjs(booking?.appointmentDate).format("dddd");
      dayMap[day] = (dayMap[day] || 0) + 1;

      const hour = parseHourFromStartTime(booking?.startTime);
      const band = hour === null ? "Other" : getTimeBand(hour);
      bandMap[band] = (bandMap[band] || 0) + 1;
    });

    const peakDayEntry = Object.entries(dayMap).sort((a, b) => b[1] - a[1])[0];
    const peakBandEntry = Object.entries(bandMap).sort((a, b) => b[1] - a[1])[0];

    const cancelledRevenueImpact = cancelled.reduce(
      (sum, booking) => sum + getBookingRevenue(booking),
      0,
    );

    return {
      addOnRate,
      addOnBookings: bookingsWithAddOn.length,
      completedBookings: completed.length,
      repeatRate,
      totalCustomers,
      repeatCustomers,
      newCustomerRevenue,
      returningCustomerRevenue,
      topServiceCategories,
      peakDay: peakDayEntry ? { label: peakDayEntry[0], count: peakDayEntry[1] } : null,
      peakTimeBand: peakBandEntry
        ? { label: peakBandEntry[0], count: peakBandEntry[1] }
        : null,
      cancelledCount: cancelled.length,
      cancelledRevenueImpact,
    };
  };

  const getProductInsights = () => {
    const bestSellingThisMonth = (salesData?.sales || [])
      .slice()
      .sort((a, b) => Number(b.quantitySold || 0) - Number(a.quantitySold || 0))
      .slice(0, 5);

    const performance = performanceData?.performance || [];

    const slowMovers = performance
      .filter(
        (p) =>
          Number(p.currentStock || 0) > 0 &&
          Number(p.periodSales || 0) <= 1,
      )
      .sort((a, b) => Number(b.currentStock || 0) - Number(a.currentStock || 0))
      .slice(0, 5);

    const lowStockRisk = performance
      .filter(
        (p) =>
          Number(p.currentStock || 0) > 0 &&
          Number(p.daysOfStockRemaining || 999) !== 999 &&
          Number(p.daysOfStockRemaining || 999) <= 14,
      )
      .sort(
        (a, b) =>
          Number(a.daysOfStockRemaining || 999) -
          Number(b.daysOfStockRemaining || 999),
      )
      .slice(0, 5);

    return {
      bestSellingThisMonth,
      slowMovers,
      lowStockRisk,
    };
  };

  const metrics = calculateMetrics();
  const serviceInsights = getServiceInsights();
  const productInsights = getProductInsights();
  const productRevenueNet = Number(salesData?.totalRevenue || 0);
  const serviceRevenueGross = Number(metrics.serviceRevenue || 0);
  const revenueMixTotal = serviceRevenueGross + productRevenueNet;
  const serviceMixPct = revenueMixTotal > 0 ? (serviceRevenueGross / revenueMixTotal) * 100 : 0;
  const productMixPct = revenueMixTotal > 0 ? (productRevenueNet / revenueMixTotal) * 100 : 0;

  if (loading) {
    return (
      <SidebarLayout>
        <div className="analytics-page" style={{ padding: "40px", textAlign: "center" }}>
          <div
            className="spinner"
            style={{
              width: "48px",
              height: "48px",
              border: "4px solid #f3f4f6",
              borderTop: "4px solid #3b82f6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }}
          ></div>
          <p style={{ color: "#6b7280" }}>Loading analytics data...</p>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div
        className="analytics-page"
        style={{ padding: "24px", maxWidth: "1600px", margin: "0 auto" }}
      >
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <h1
            style={{
              fontSize: "32px",
              fontWeight: "700",
              marginBottom: "8px",
              background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            📊 Business Analytics
          </h1>
          <p style={{ color: "#6b7280", fontSize: "16px" }}>
            Comprehensive insights into your business performance
          </p>
        </div>

        {/* Tab Navigation */}
        <div
          style={{
            display: "flex",
            gap: "10px",
            marginBottom: "24px",
            borderBottom: "2px solid #e5e7eb",
          }}
        >
          <button
            onClick={() => setActiveTab("services")}
            style={{
              padding: "12px 24px",
              background: activeTab === "services" ? "#001d4a" : "transparent",
              color: activeTab === "services" ? "#fff" : "#666",
              border: "none",
              borderBottom:
                activeTab === "services"
                  ? "3px solid #001d4a"
                  : "3px solid transparent",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "600",
              transition: "all 0.2s",
            }}
          >
            📊 Service Analytics
          </button>
          <button
            onClick={() => setActiveTab("products")}
            style={{
              padding: "12px 24px",
              background: activeTab === "products" ? "#001d4a" : "transparent",
              color: activeTab === "products" ? "#fff" : "#666",
              border: "none",
              borderBottom:
                activeTab === "products"
                  ? "3px solid #001d4a"
                  : "3px solid transparent",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "600",
              transition: "all 0.2s",
            }}
          >
            📦 Product Analytics
          </button>
          {/* Business Payouts tab hidden — use /dashboard/payouts dedicated page */}
        </div>

        {/* Filters */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            marginBottom: "32px",
            flexWrap: "wrap",
            background: "white",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <div style={{ flex: "1", minWidth: "200px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                marginBottom: "8px",
                color: "#374151",
              }}
            >
              Date Range
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              style={{
                width: "100%",
                padding: "10px",
                border: "2px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              <option value="currentMonth">This Month</option>
              <option value="currentYear">This Year</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
              <option value="year">Last Year</option>
              <option value="all">All Time</option>
            </select>
          </div>

          {/* Hide location filter for payouts tab */}
          {activeTab !== "payouts" && (
            <div style={{ flex: "1", minWidth: "200px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "#374151",
                }}
              >
                Location
              </label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                <option value="all">All Locations</option>
                {locations.map((loc) => (
                  <option key={loc._id} value={loc._id}>
                    {loc.name}
                    {loc.city ? ` - ${loc.city}` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            onClick={
              activeTab === "payouts" ? fetchPayoutAnalytics : fetchAllData
            }
            style={{
              padding: "10px 24px",
              background: "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              alignSelf: "flex-end",
            }}
          >
            🔄 Refresh Data
          </button>
        </div>

        {/* Service Analytics Tab */}
        {activeTab === "services" && (
          <>
            {/* Key Metrics Cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  window.innerWidth <= 768 ? "1fr" : "repeat(4, 1fr)",
                gap: window.innerWidth <= 768 ? "16px" : "24px",
                marginBottom: window.innerWidth <= 768 ? "24px" : "32px",
              }}
            >
              {/* Total Revenue */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                  padding: "24px",
                  borderRadius: "16px",
                  color: "white",
                  boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    opacity: 0.9,
                    marginBottom: "8px",
                    fontWeight: "600",
                  }}
                >
                  TOTAL REVENUE
                </div>
                <div
                  style={{
                    fontSize: "42px",
                    fontWeight: "700",
                    marginBottom: "4px",
                  }}
                >
                  ${metrics.totalRevenue.toFixed(0)}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    opacity: 0.9,
                    marginBottom: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <span style={{ opacity: 0.8 }}>Business Net:</span>
                  <span style={{ fontWeight: "700", fontSize: "16px" }}>
                    ${metrics.businessRevenue.toFixed(0)}
                  </span>
                  <span style={{ opacity: 0.7, fontSize: "11px" }}>
                    (
                    {metrics.totalRevenue > 0
                      ? (
                          (metrics.businessRevenue / metrics.totalRevenue) *
                          100
                        ).toFixed(0)
                      : 0}
                    %)
                  </span>
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    opacity: 0.8,
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  {metrics.revenueGrowth >= 0 ? "📈" : "📉"}
                  <span style={{ fontWeight: "600" }}>
                    {metrics.revenueGrowth >= 0 ? "+" : ""}
                    {metrics.revenueGrowth.toFixed(1)}%
                  </span>
                  <span>vs previous period</span>
                </div>
              </div>

              {/* Completed Bookings */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  padding: "24px",
                  borderRadius: "16px",
                  color: "white",
                  boxShadow: "0 10px 30px rgba(16, 185, 129, 0.3)",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    opacity: 0.9,
                    marginBottom: "8px",
                    fontWeight: "600",
                  }}
                >
                  COMPLETED BOOKINGS
                </div>
                <div
                  style={{
                    fontSize: "42px",
                    fontWeight: "700",
                    marginBottom: "4px",
                  }}
                >
                  {metrics.completedBookings}
                </div>
                <div style={{ fontSize: "13px", opacity: 0.8 }}>
                  {metrics.completionRate.toFixed(1)}% completion rate
                </div>
              </div>

              {/* Average Booking Value */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                  padding: "24px",
                  borderRadius: "16px",
                  color: "white",
                  boxShadow: "0 10px 30px rgba(59, 130, 246, 0.3)",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    opacity: 0.9,
                    marginBottom: "8px",
                    fontWeight: "600",
                  }}
                >
                  AVG BOOKING VALUE
                </div>
                <div
                  style={{
                    fontSize: "42px",
                    fontWeight: "700",
                    marginBottom: "4px",
                  }}
                >
                  ${metrics.avgBookingValue.toFixed(0)}
                </div>
                <div style={{ fontSize: "13px", opacity: 0.8 }}>
                  Per completed booking
                </div>
              </div>

              {/* Total Bookings */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  padding: "24px",
                  borderRadius: "16px",
                  color: "white",
                  boxShadow: "0 10px 30px rgba(245, 158, 11, 0.3)",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    opacity: 0.9,
                    marginBottom: "8px",
                    fontWeight: "600",
                  }}
                >
                  TOTAL BOOKINGS
                </div>
                <div
                  style={{
                    fontSize: "42px",
                    fontWeight: "700",
                    marginBottom: "4px",
                  }}
                >
                  {metrics.totalBookings}
                </div>
                <div style={{ fontSize: "13px", opacity: 0.8 }}>
                  All statuses included
                </div>
              </div>
            </div>

            {/* Revenue Breakdown */}
            <div
              style={{
                background: "white",
                padding: "24px",
                borderRadius: "16px",
                marginBottom: "32px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <h3
                style={{
                  marginBottom: "8px",
                  fontSize: "20px",
                  fontWeight: "700",
                }}
              >
                💵 Revenue Breakdown
              </h3>
              <p
                style={{
                  fontSize: "13px",
                  color: "#6b7280",
                  marginBottom: "16px",
                }}
              >
                Gross revenue from customers (before professional commission)
              </p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    window.innerWidth <= 768
                      ? "1fr"
                      : "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "16px",
                }}
              >
                <div
                  style={{
                    padding: "20px",
                    background: "#f0f9ff",
                    borderRadius: "12px",
                    border: "2px solid #3b82f6",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#1e40af",
                      fontWeight: "600",
                      marginBottom: "8px",
                    }}
                  >
                    Services
                  </div>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: "700",
                      color: "#1e3a8a",
                    }}
                  >
                    ${metrics.serviceRevenue.toFixed(2)}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#3b82f6",
                      marginTop: "4px",
                    }}
                  >
                    {metrics.totalRevenue > 0
                      ? (
                          (metrics.serviceRevenue / metrics.totalRevenue) *
                          100
                        ).toFixed(1)
                      : 0}
                    % of total
                  </div>
                </div>

                <div
                  style={{
                    padding: "20px",
                    background: "#fef3c7",
                    borderRadius: "12px",
                    border: "2px solid #f59e0b",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#92400e",
                      fontWeight: "600",
                      marginBottom: "8px",
                    }}
                  >
                    Tips
                  </div>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: "700",
                      color: "#78350f",
                    }}
                  >
                    ${metrics.tipRevenue.toFixed(2)}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#f59e0b",
                      marginTop: "4px",
                    }}
                  >
                    {metrics.totalRevenue > 0
                      ? (
                          (metrics.tipRevenue / metrics.totalRevenue) *
                          100
                        ).toFixed(1)
                      : 0}
                    % of total
                  </div>
                </div>
              </div>
            </div>

            {/* Service + Product Revenue Mix */}
            <div
              style={{
                background: "white",
                padding: "24px",
                borderRadius: "16px",
                marginBottom: "32px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <h3
                style={{
                  marginBottom: "8px",
                  fontSize: "20px",
                  fontWeight: "700",
                }}
              >
                🧭 Revenue Split: Services vs Products
              </h3>
              <p
                style={{
                  fontSize: "13px",
                  color: "#6b7280",
                  marginBottom: "16px",
                }}
              >
                Snapshot of your business model for this date range
              </p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    window.innerWidth <= 768 ? "1fr" : "repeat(3, 1fr)",
                  gap: "16px",
                }}
              >
                <div
                  style={{
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #3b82f6",
                    background: "#eff6ff",
                  }}
                >
                  <div style={{ fontSize: "13px", color: "#1e40af", fontWeight: "600" }}>
                    Services
                  </div>
                  <div style={{ fontSize: "28px", fontWeight: "700", color: "#1e3a8a" }}>
                    {serviceMixPct.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: "12px", color: "#1e40af" }}>
                    ${serviceRevenueGross.toFixed(2)}
                  </div>
                </div>

                <div
                  style={{
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #10b981",
                    background: "#ecfdf5",
                  }}
                >
                  <div style={{ fontSize: "13px", color: "#047857", fontWeight: "600" }}>
                    Products (Net)
                  </div>
                  <div style={{ fontSize: "28px", fontWeight: "700", color: "#065f46" }}>
                    {productMixPct.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: "12px", color: "#047857" }}>
                    ${productRevenueNet.toFixed(2)}
                  </div>
                </div>

                <div
                  style={{
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #d1d5db",
                    background: "#f9fafb",
                  }}
                >
                  <div style={{ fontSize: "13px", color: "#374151", fontWeight: "600" }}>
                    Combined Total
                  </div>
                  <div style={{ fontSize: "28px", fontWeight: "700", color: "#111827" }}>
                    ${revenueMixTotal.toFixed(2)}
                  </div>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    Services + Products
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Row 1 */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  window.innerWidth <= 768
                    ? "1fr"
                    : "repeat(auto-fit, minmax(400px, 1fr))",
                gap: window.innerWidth <= 768 ? "16px" : "24px",
                marginBottom: window.innerWidth <= 768 ? "24px" : "32px",
              }}
            >
              {/* Revenue Over Time */}
              <div
                style={{
                  background: "white",
                  padding: "24px",
                  borderRadius: "16px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "20px",
                    fontSize: "18px",
                    fontWeight: "700",
                  }}
                >
                  📈 Revenue Trend
                </h3>
                <div style={{ height: "300px" }}>
                  <Line
                    data={getRevenueChartData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: (context) =>
                              `Revenue: $${context.parsed.y.toFixed(2)}`,
                          },
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: (value) => "$" + value,
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>

              {/* Booking Status Distribution */}
              <div
                style={{
                  background: "white",
                  padding: "24px",
                  borderRadius: "16px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "20px",
                    fontSize: "18px",
                    fontWeight: "700",
                  }}
                >
                  📊 Booking Status Distribution
                </h3>
                <div
                  style={{
                    height: "300px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Doughnut
                    data={getStatusDistributionData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "bottom",
                        },
                        tooltip: {
                          callbacks: {
                            label: (context) => {
                              const label = context.label || "";
                              const value = context.parsed || 0;
                              const total = context.dataset.data.reduce(
                                (a, b) => a + b,
                                0,
                              );
                              const percentage = (
                                (value / total) *
                                100
                              ).toFixed(1);
                              return `${label}: ${value} (${percentage}%)`;
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Charts Row 2 */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  window.innerWidth <= 768
                    ? "1fr"
                    : "repeat(auto-fit, minmax(400px, 1fr))",
                gap: window.innerWidth <= 768 ? "16px" : "24px",
                marginBottom: window.innerWidth <= 768 ? "24px" : "32px",
              }}
            >
              {/* Top Services */}
              <div
                style={{
                  background: "white",
                  padding: "24px",
                  borderRadius: "16px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "20px",
                    fontSize: "18px",
                    fontWeight: "700",
                  }}
                >
                  ✂️ Top Services by Revenue
                </h3>
                <div style={{ height: "350px" }}>
                  <Bar
                    data={getTopServicesData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      indexAxis: "y",
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: (context) =>
                              `Revenue: $${context.parsed.x.toFixed(2)}`,
                          },
                        },
                      },
                      scales: {
                        x: {
                          beginAtZero: true,
                          ticks: {
                            callback: (value) => "$" + value,
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>

              {/* Staff Performance */}
              <div
                style={{
                  background: "white",
                  padding: "24px",
                  borderRadius: "16px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "20px",
                    fontSize: "18px",
                    fontWeight: "700",
                  }}
                >
                  👥 Top Staff Performance
                </h3>
                <div style={{ height: "350px" }}>
                  <Bar
                    data={getStaffPerformanceData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "top",
                        },
                        tooltip: {
                          callbacks: {
                            label: (context) => {
                              const label = context.dataset.label || "";
                              if (label === "Revenue ($)") {
                                return `${label}: $${context.parsed.y.toFixed(2)}`;
                              }
                              return `${label}: ${context.parsed.y}`;
                            },
                          },
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div
              style={{
                background: "white",
                padding: "24px",
                borderRadius: "16px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <h3
                style={{
                  marginBottom: "20px",
                  fontSize: "20px",
                  fontWeight: "700",
                }}
              >
                📋 Quick Stats
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "16px",
                }}
              >
                <div
                  style={{
                    padding: "16px",
                    background: "#fef2f2",
                    borderRadius: "12px",
                    borderLeft: "4px solid #ef4444",
                    flex: "1",
                    minWidth: "150px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#991b1b",
                      fontWeight: "600",
                      marginBottom: "4px",
                    }}
                  >
                    Cancelled
                  </div>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: "700",
                      color: "#b91c1c",
                    }}
                  >
                    {metrics.cancelledBookings}
                  </div>
                </div>

                <div
                  style={{
                    padding: "16px",
                    background: "#f3f4f6",
                    borderRadius: "12px",
                    borderLeft: "4px solid #6b7280",
                    flex: "1",
                    minWidth: "150px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#374151",
                      fontWeight: "600",
                      marginBottom: "4px",
                    }}
                  >
                    No-Shows
                  </div>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: "700",
                      color: "#4b5563",
                    }}
                  >
                    {metrics.noShowBookings}
                  </div>
                </div>

                <div
                  style={{
                    padding: "16px",
                    background: "#f0fdf4",
                    borderRadius: "12px",
                    borderLeft: "4px solid #10b981",
                    flex: "1",
                    minWidth: "150px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#15803d",
                      fontWeight: "600",
                      marginBottom: "4px",
                    }}
                  >
                    Active Staff
                  </div>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: "700",
                      color: "#166534",
                    }}
                  >
                    {
                      staff.filter(
                        (s) =>
                          s.status === "Active" &&
                          s.isActive !== false &&
                          s.role !== "R1", // Exclude owners from staff count
                      ).length
                    }
                  </div>
                </div>

                <div
                  style={{
                    padding: "16px",
                    background: "#eff6ff",
                    borderRadius: "12px",
                    borderLeft: "4px solid #3b82f6",
                    flex: "1",
                    minWidth: "150px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#1e40af",
                      fontWeight: "600",
                      marginBottom: "4px",
                    }}
                  >
                    Active Services
                  </div>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: "700",
                      color: "#1e3a8a",
                    }}
                  >
                    {services.filter((s) => s.isActive).length}
                  </div>
                </div>

                <div
                  style={{
                    padding: "16px",
                    background: "#faf5ff",
                    borderRadius: "12px",
                    borderLeft: "4px solid #8b5cf6",
                    flex: "1",
                    minWidth: "150px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#6b21a8",
                      fontWeight: "600",
                      marginBottom: "4px",
                    }}
                  >
                    Locations
                  </div>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: "700",
                      color: "#7c3aed",
                    }}
                  >
                    {locations.length}
                  </div>
                </div>
              </div>
            </div>

            {/* Owner-Friendly Service Insights */}
            <div
              style={{
                background: "white",
                padding: "24px",
                borderRadius: "16px",
                marginTop: "24px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <h3 style={{ marginBottom: "16px", fontSize: "20px", fontWeight: "700" }}>
                💡 Service Insights
              </h3>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    window.innerWidth <= 768 ? "1fr" : "repeat(3, 1fr)",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <div style={{ padding: "16px", background: "#f8fafc", borderRadius: "12px" }}>
                  <div style={{ fontSize: "13px", color: "#64748b", fontWeight: "600" }}>
                    Add-On Success Rate
                  </div>
                  <div style={{ fontSize: "28px", fontWeight: "700", color: "#0f172a" }}>
                    {serviceInsights.addOnRate.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>
                    {serviceInsights.addOnBookings} of {serviceInsights.completedBookings} completed bookings
                  </div>
                </div>

                <div style={{ padding: "16px", background: "#f8fafc", borderRadius: "12px" }}>
                  <div style={{ fontSize: "13px", color: "#64748b", fontWeight: "600" }}>
                    Repeat Customer Rate
                  </div>
                  <div style={{ fontSize: "28px", fontWeight: "700", color: "#0f172a" }}>
                    {serviceInsights.repeatRate.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>
                    {serviceInsights.repeatCustomers} repeat out of {serviceInsights.totalCustomers} customers
                  </div>
                </div>

                <div style={{ padding: "16px", background: "#f8fafc", borderRadius: "12px" }}>
                  <div style={{ fontSize: "13px", color: "#64748b", fontWeight: "600" }}>
                    Cancellation Impact
                  </div>
                  <div style={{ fontSize: "28px", fontWeight: "700", color: "#b91c1c" }}>
                    ${serviceInsights.cancelledRevenueImpact.toFixed(2)}
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>
                    {serviceInsights.cancelledCount} cancelled bookings
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    window.innerWidth <= 768 ? "1fr" : "repeat(2, 1fr)",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <div style={{ padding: "16px", border: "1px solid #e5e7eb", borderRadius: "12px" }}>
                  <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "10px" }}>
                    New vs Returning Revenue
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                    <span style={{ color: "#64748b", fontSize: "13px" }}>New customers</span>
                    <span style={{ fontWeight: "700", color: "#1e3a8a" }}>
                      ${serviceInsights.newCustomerRevenue.toFixed(2)}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "#64748b", fontSize: "13px" }}>Returning customers</span>
                    <span style={{ fontWeight: "700", color: "#065f46" }}>
                      ${serviceInsights.returningCustomerRevenue.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div style={{ padding: "16px", border: "1px solid #e5e7eb", borderRadius: "12px" }}>
                  <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "10px" }}>
                    Peak Booking Times
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                    <span style={{ color: "#64748b", fontSize: "13px" }}>Busiest day</span>
                    <span style={{ fontWeight: "700" }}>
                      {serviceInsights.peakDay
                        ? `${serviceInsights.peakDay.label} (${serviceInsights.peakDay.count})`
                        : "-"}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "#64748b", fontSize: "13px" }}>Busiest time</span>
                    <span style={{ fontWeight: "700" }}>
                      {serviceInsights.peakTimeBand
                        ? `${serviceInsights.peakTimeBand.label} (${serviceInsights.peakTimeBand.count})`
                        : "-"}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ padding: "16px", border: "1px solid #e5e7eb", borderRadius: "12px" }}>
                <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "10px" }}>
                  Top Earning Service Categories
                </div>
                {serviceInsights.topServiceCategories.length === 0 ? (
                  <div style={{ color: "#9ca3af", fontSize: "13px" }}>No service category data yet</div>
                ) : (
                  serviceInsights.topServiceCategories.map((cat, idx) => (
                    <div
                      key={`${cat.category}-${idx}`}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "8px 0",
                        borderBottom:
                          idx < serviceInsights.topServiceCategories.length - 1
                            ? "1px solid #f3f4f6"
                            : "none",
                      }}
                    >
                      <span style={{ color: "#374151", fontSize: "13px" }}>
                        {cat.category}
                      </span>
                      <span style={{ fontWeight: "700", color: "#111827", fontSize: "13px" }}>
                        ${cat.revenue.toFixed(2)} ({cat.bookings} bookings)
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {/* Product Analytics Tab - Complete UI from ProductAnalytics.js */}
        {activeTab === "products" && (
          <>
            {/* Key Metrics Cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  window.innerWidth <= 768
                    ? "1fr"
                    : "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "16px",
                marginBottom: "32px",
              }}
            >
              {/* Total Revenue */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                  padding: "24px",
                  borderRadius: "16px",
                  color: "white",
                  boxShadow: "0 10px 30px rgba(59, 130, 246, 0.3)",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    opacity: 0.9,
                    marginBottom: "8px",
                    fontWeight: "600",
                  }}
                >
                  TOTAL REVENUE
                </div>
                <div
                  style={{
                    fontSize: "36px",
                    fontWeight: "700",
                    marginBottom: "4px",
                  }}
                >
                  ${salesData?.totalRevenue?.toFixed(0) || 0}
                </div>
                <div style={{ fontSize: "13px", opacity: 0.8 }}>
                  {salesData?.totalOrders || 0} orders
                </div>
              </div>

              {/* Total Products */}
              <div
                style={{
                  background: "white",
                  padding: "24px",
                  borderRadius: "16px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    color: "#6b7280",
                    marginBottom: "8px",
                    fontWeight: "600",
                  }}
                >
                  ACTIVE PRODUCTS
                </div>
                <div
                  style={{
                    fontSize: "36px",
                    fontWeight: "700",
                    color: "#1f2937",
                    marginBottom: "4px",
                  }}
                >
                  {performanceData?.summary?.totalProducts || 0}
                </div>
                <div style={{ fontSize: "13px", color: "#10b981" }}>
                  {inventoryReport?.summary?.inStock || 0} in stock
                </div>
              </div>

              {/* Stock Value */}
              <div
                style={{
                  background: "white",
                  padding: "24px",
                  borderRadius: "16px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    color: "#6b7280",
                    marginBottom: "8px",
                    fontWeight: "600",
                  }}
                >
                  STOCK VALUE
                </div>
                <div
                  style={{
                    fontSize: "36px",
                    fontWeight: "700",
                    color: "#1f2937",
                    marginBottom: "4px",
                  }}
                >
                  ${performanceData?.summary?.totalStockValue?.toFixed(0) || 0}
                </div>
                <div style={{ fontSize: "13px", color: "#f59e0b" }}>
                  {inventoryReport?.summary?.lowStock || 0} low stock items
                </div>
              </div>

              {/* Avg Velocity */}
              <div
                style={{
                  background: "white",
                  padding: "24px",
                  borderRadius: "16px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    color: "#6b7280",
                    marginBottom: "8px",
                    fontWeight: "600",
                  }}
                >
                  AVG SALES VELOCITY
                </div>
                <div
                  style={{
                    fontSize: "36px",
                    fontWeight: "700",
                    color: "#1f2937",
                    marginBottom: "4px",
                  }}
                >
                  {performanceData?.summary?.avgSalesVelocity || "0.00"}
                </div>
                <div style={{ fontSize: "13px", color: "#6b7280" }}>
                  units per day
                </div>
              </div>
            </div>

            {/* Charts Row 1 */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  window.innerWidth <= 768 ? "1fr" : "repeat(2, 1fr)",
                gap: "24px",
                marginBottom: "32px",
              }}
            >
              {/* Top Selling Products */}
              <div
                style={{
                  background: "white",
                  padding: "24px",
                  borderRadius: "16px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "20px",
                    fontSize: "18px",
                    fontWeight: "700",
                  }}
                >
                  🏆 Top Selling Products
                </h3>
                <div
                  style={{
                    height: "300px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {topProductsChartData() ? (
                    <Bar
                      data={topProductsChartData()}
                      options={productChartOptions}
                    />
                  ) : (
                    <div style={{ textAlign: "center", color: "#9ca3af" }}>
                      <div style={{ fontSize: "48px", marginBottom: "12px" }}>
                        📊
                      </div>
                      <p>No sales data available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Category Revenue */}
              <div
                style={{
                  background: "white",
                  padding: "24px",
                  borderRadius: "16px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "20px",
                    fontSize: "18px",
                    fontWeight: "700",
                  }}
                >
                  📦 Revenue by Category
                </h3>
                <div
                  style={{
                    height: "300px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {categoryRevenueChartData() ? (
                    <Doughnut
                      data={categoryRevenueChartData()}
                      options={{
                        ...productChartOptions,
                        scales: undefined,
                      }}
                    />
                  ) : (
                    <div style={{ textAlign: "center", color: "#9ca3af" }}>
                      <div style={{ fontSize: "48px", marginBottom: "12px" }}>
                        📦
                      </div>
                      <p>No category data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Charts Row 2 */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  window.innerWidth <= 768 ? "1fr" : "repeat(2, 1fr)",
                gap: "24px",
                marginBottom: "32px",
              }}
            >
              {/* Sales Velocity */}
              <div
                style={{
                  background: "white",
                  padding: "24px",
                  borderRadius: "16px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "20px",
                    fontSize: "18px",
                    fontWeight: "700",
                  }}
                >
                  ⚡ Sales Velocity (Top Products)
                </h3>
                <div
                  style={{
                    height: "300px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {velocityChartData() ? (
                    <Bar
                      data={velocityChartData()}
                      options={productChartOptions}
                    />
                  ) : (
                    <div style={{ textAlign: "center", color: "#9ca3af" }}>
                      <div style={{ fontSize: "48px", marginBottom: "12px" }}>
                        ⚡
                      </div>
                      <p>No velocity data available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Stock Status */}
              <div
                style={{
                  background: "white",
                  padding: "24px",
                  borderRadius: "16px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "20px",
                    fontSize: "18px",
                    fontWeight: "700",
                  }}
                >
                  📊 Inventory Status
                </h3>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    height: "300px",
                    justifyContent: "center",
                  }}
                >
                  {/* In Stock */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          width: "12px",
                          height: "12px",
                          background: "#10b981",
                          borderRadius: "50%",
                        }}
                      />
                      <span style={{ fontSize: "16px", fontWeight: "600" }}>
                        In Stock
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: "28px",
                        fontWeight: "700",
                        color: "#10b981",
                      }}
                    >
                      {inventoryReport?.summary?.inStock || 0}
                    </span>
                  </div>

                  {/* Low Stock */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          width: "12px",
                          height: "12px",
                          background: "#f59e0b",
                          borderRadius: "50%",
                        }}
                      />
                      <span style={{ fontSize: "16px", fontWeight: "600" }}>
                        Low Stock
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: "28px",
                        fontWeight: "700",
                        color: "#f59e0b",
                      }}
                    >
                      {inventoryReport?.summary?.lowStock || 0}
                    </span>
                  </div>

                  {/* Out of Stock */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          width: "12px",
                          height: "12px",
                          background: "#ef4444",
                          borderRadius: "50%",
                        }}
                      />
                      <span style={{ fontSize: "16px", fontWeight: "600" }}>
                        Out of Stock
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: "28px",
                        fontWeight: "700",
                        color: "#ef4444",
                      }}
                    >
                      {inventoryReport?.summary?.outOfStock || 0}
                    </span>
                  </div>

                  {/* Inactive */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          width: "12px",
                          height: "12px",
                          background: "#9ca3af",
                          borderRadius: "50%",
                        }}
                      />
                      <span style={{ fontSize: "16px", fontWeight: "600" }}>
                        Inactive
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: "28px",
                        fontWeight: "700",
                        color: "#9ca3af",
                      }}
                    >
                      {inventoryReport?.summary?.inactiveProducts || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Performance Table */}
            <div
              style={{
                background: "white",
                padding: "24px",
                borderRadius: "16px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <h3
                style={{
                  marginBottom: "20px",
                  fontSize: "18px",
                  fontWeight: "700",
                }}
              >
                📈 Product Performance Details
              </h3>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr
                      style={{
                        background: "#f9fafb",
                        borderBottom: "2px solid #e5e7eb",
                      }}
                    >
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "#6b7280",
                        }}
                      >
                        PRODUCT
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "#6b7280",
                        }}
                      >
                        REVENUE
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "#6b7280",
                        }}
                      >
                        SOLD
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "#6b7280",
                        }}
                      >
                        STOCK
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "#6b7280",
                        }}
                      >
                        VELOCITY
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "#6b7280",
                        }}
                      >
                        TURNOVER
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "#6b7280",
                        }}
                      >
                        DAYS LEFT
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {performanceData?.performance
                      ?.slice(0, 20)
                      .map((product, index) => (
                        <tr
                          key={index}
                          style={{ borderBottom: "1px solid #e5e7eb" }}
                        >
                          <td style={{ padding: "12px" }}>
                            <div
                              style={{ fontWeight: "600", color: "#1f2937" }}
                            >
                              {product.name}
                            </div>
                            <div style={{ fontSize: "12px", color: "#6b7280" }}>
                              {Array.isArray(product.category)
                                ? product.category.join(", ")
                                : product.category}
                            </div>
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              fontWeight: "600",
                              color: "#10b981",
                            }}
                          >
                            ${product.periodRevenue.toFixed(2)}
                          </td>
                          <td style={{ padding: "12px", textAlign: "right" }}>
                            {product.periodSales}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              color:
                                product.currentStock === 0
                                  ? "#ef4444"
                                  : "#1f2937",
                            }}
                          >
                            {product.currentStock}
                          </td>
                          <td style={{ padding: "12px", textAlign: "right" }}>
                            {product.salesVelocity}/day
                          </td>
                          <td style={{ padding: "12px", textAlign: "right" }}>
                            {product.turnoverRate}x
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              color:
                                product.daysOfStockRemaining < 7
                                  ? "#ef4444"
                                  : "#1f2937",
                            }}
                          >
                            {product.daysOfStockRemaining === 999
                              ? "∞"
                              : `${product.daysOfStockRemaining}d`}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Owner-Friendly Product Insights */}
            <div
              style={{
                background: "white",
                padding: "24px",
                borderRadius: "16px",
                marginTop: "24px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <h3 style={{ marginBottom: "16px", fontSize: "20px", fontWeight: "700" }}>
                💡 Product Insights
              </h3>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    window.innerWidth <= 768 ? "1fr" : "repeat(3, 1fr)",
                  gap: "16px",
                }}
              >
                <div style={{ border: "1px solid #e5e7eb", borderRadius: "12px", padding: "16px" }}>
                  <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "10px" }}>
                    Best-Selling Products (This Range)
                  </div>
                  {productInsights.bestSellingThisMonth.length === 0 ? (
                    <div style={{ color: "#9ca3af", fontSize: "13px" }}>No product sales yet</div>
                  ) : (
                    productInsights.bestSellingThisMonth.map((p, idx) => (
                      <div
                        key={`${p.productId || p.name}-${idx}`}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "8px 0",
                          borderBottom:
                            idx < productInsights.bestSellingThisMonth.length - 1
                              ? "1px solid #f3f4f6"
                              : "none",
                        }}
                      >
                        <span style={{ color: "#374151", fontSize: "13px" }}>{p.name}</span>
                        <span style={{ fontWeight: "700", fontSize: "13px" }}>{p.quantitySold} sold</span>
                      </div>
                    ))
                  )}
                </div>

                <div style={{ border: "1px solid #e5e7eb", borderRadius: "12px", padding: "16px" }}>
                  <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "10px" }}>
                    Slow Movers (Needs Attention)
                  </div>
                  {productInsights.slowMovers.length === 0 ? (
                    <div style={{ color: "#9ca3af", fontSize: "13px" }}>No slow movers detected</div>
                  ) : (
                    productInsights.slowMovers.map((p, idx) => (
                      <div
                        key={`${p.productId || p.name}-${idx}`}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "8px 0",
                          borderBottom:
                            idx < productInsights.slowMovers.length - 1
                              ? "1px solid #f3f4f6"
                              : "none",
                        }}
                      >
                        <span style={{ color: "#374151", fontSize: "13px" }}>{p.name}</span>
                        <span style={{ fontWeight: "700", fontSize: "13px" }}>
                          {p.currentStock} in stock, {p.periodSales} sold
                        </span>
                      </div>
                    ))
                  )}
                </div>

                <div style={{ border: "1px solid #e5e7eb", borderRadius: "12px", padding: "16px" }}>
                  <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "10px" }}>
                    Low Stock Risk (Next 14 Days)
                  </div>
                  {productInsights.lowStockRisk.length === 0 ? (
                    <div style={{ color: "#9ca3af", fontSize: "13px" }}>No immediate stock risk</div>
                  ) : (
                    productInsights.lowStockRisk.map((p, idx) => (
                      <div
                        key={`${p.productId || p.name}-${idx}`}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "8px 0",
                          borderBottom:
                            idx < productInsights.lowStockRisk.length - 1
                              ? "1px solid #f3f4f6"
                              : "none",
                        }}
                      >
                        <span style={{ color: "#374151", fontSize: "13px" }}>{p.name}</span>
                        <span style={{ fontWeight: "700", fontSize: "13px", color: "#b91c1c" }}>
                          {p.daysOfStockRemaining}d left
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Business Payouts Tab */}
        {activeTab === "payouts" && (
          <>
            {payoutLoading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "48px",
                  color: "#6b7280",
                }}
              >
                Loading payout data...
              </div>
            ) : canonicalReconciliation ? (
              /* ================================================================
                 CANONICAL VIEW — shown when PAYOUT_FLAG_READ_API is enabled.
                 Mirrors the held / released / history layout that R3 & R4 see.
                 ================================================================ */
              (() => {
                const rec = canonicalReconciliation;
                const heldEntries = rec.held?.entries || [];
                const releasedEntries = rec.released?.entries || [];
                const history = rec.history || [];
                const heldTotal = rec.held?.total || 0;
                const releasedTotal = rec.released?.total || 0;
                const paidThisMonth = rec.totalPaidThisMonth || 0;
                const fmt = (n) => `$${(n || 0).toFixed(2)}`;

                const sourceLabel = (sourceType) => {
                  const map = {
                    booking_service: "Booking — Service",
                    booking_product: "Booking — Product",
                    order_product: "Order — Product",
                    booth_rent: "Booth Rent",
                    package_shop_share: "Package — Shop share",
                    package_session: "Package — Session",
                    manual: "Manual",
                  };
                  return map[sourceType] || sourceType || "—";
                };

                const formatCountdown = (releaseDate) => {
                  if (!releaseDate) return "—";
                  const diff = new Date(releaseDate) - new Date();
                  if (diff <= 0) return "Releasing soon...";
                  const hours = Math.floor(diff / (1000 * 60 * 60));
                  const mins = Math.floor(
                    (diff % (1000 * 60 * 60)) / (1000 * 60),
                  );
                  if (hours > 24)
                    return `${Math.floor(hours / 24)}d ${hours % 24}h`;
                  return `${hours}h ${mins}m`;
                };

                const togglePayout = (key) =>
                  setPayoutExpandedSections((prev) => ({
                    ...prev,
                    [key]: !prev[key],
                  }));

                return (
                  <>
                    {/* Canonical badge */}
                    <div style={{ marginBottom: "16px" }}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "3px 12px",
                          background: "#dcfce7",
                          color: "#166534",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        ✓ Live Ledger
                      </span>
                    </div>

                    {/* Summary Cards */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: "16px",
                        marginBottom: "28px",
                      }}
                    >
                      <div
                        style={{
                          padding: "20px",
                          background:
                            "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                          borderRadius: "12px",
                          color: "white",
                          boxShadow: "0 4px 6px rgba(245,158,11,0.3)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            opacity: 0.9,
                            marginBottom: "8px",
                          }}
                        >
                          🔒 IN HOLD WINDOW
                        </div>
                        <div
                          style={{
                            fontSize: "30px",
                            fontWeight: "bold",
                            lineHeight: 1,
                          }}
                        >
                          {fmt(heldTotal)}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            marginTop: "8px",
                            opacity: 0.9,
                          }}
                        >
                          {heldEntries.length} holding
                        </div>
                      </div>

                      <div
                        style={{
                          padding: "20px",
                          background:
                            "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                          borderRadius: "12px",
                          color: "white",
                          boxShadow: "0 4px 6px rgba(59,130,246,0.3)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            opacity: 0.9,
                            marginBottom: "8px",
                          }}
                        >
                          📤 QUEUED FOR TRANSFER
                        </div>
                        <div
                          style={{
                            fontSize: "30px",
                            fontWeight: "bold",
                            lineHeight: 1,
                          }}
                        >
                          {fmt(releasedTotal)}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            marginTop: "8px",
                            opacity: 0.9,
                          }}
                        >
                          {rec.nextPayoutDate || "Awaiting next batch"}
                        </div>
                      </div>

                      <div
                        style={{
                          padding: "20px",
                          background:
                            "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                          borderRadius: "12px",
                          color: "white",
                          boxShadow: "0 4px 6px rgba(16,185,129,0.3)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            opacity: 0.9,
                            marginBottom: "8px",
                          }}
                        >
                          ✅ PAID THIS MONTH
                        </div>
                        <div
                          style={{
                            fontSize: "30px",
                            fontWeight: "bold",
                            lineHeight: 1,
                          }}
                        >
                          {fmt(paidThisMonth)}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            marginTop: "8px",
                            opacity: 0.9,
                          }}
                        >
                          {
                            history.filter((h) => {
                              const d = new Date(h.payoutDate);
                              const n = new Date();
                              return (
                                d.getMonth() === n.getMonth() &&
                                d.getFullYear() === n.getFullYear()
                              );
                            }).length
                          }{" "}
                          payout(s)
                        </div>
                      </div>
                    </div>

                    {/* Held */}
                    <div
                      style={{
                        background: "white",
                        borderRadius: "12px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                        marginBottom: "16px",
                      }}
                    >
                      <div
                        onClick={() => togglePayout("held")}
                        style={{
                          padding: "16px 20px",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderBottom: payoutExpandedSections.held
                            ? "1px solid #e5e7eb"
                            : "none",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <span style={{ fontSize: "20px" }}>🔒</span>
                          <div>
                            <div
                              style={{ fontWeight: "600", fontSize: "16px" }}
                            >
                              Held (In Hold Window)
                            </div>
                            <div style={{ fontSize: "13px", color: "#6b7280" }}>
                              {heldEntries.length} entr
                              {heldEntries.length !== 1 ? "ies" : "y"} ·{" "}
                              {fmt(heldTotal)}
                            </div>
                          </div>
                        </div>
                        <span style={{ fontSize: "18px", color: "#6b7280" }}>
                          {payoutExpandedSections.held ? "▼" : "▶"}
                        </span>
                      </div>
                      {payoutExpandedSections.held && (
                        <div style={{ padding: "20px" }}>
                          {heldEntries.length === 0 ? (
                            <p
                              style={{
                                color: "#9ca3af",
                                textAlign: "center",
                              }}
                            >
                              No earnings currently in hold period
                            </p>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px",
                              }}
                            >
                              {heldEntries.map((entry) => (
                                <div
                                  key={entry._id}
                                  style={{
                                    padding: "16px",
                                    background: "#fef3c7",
                                    border: "2px solid #f59e0b",
                                    borderRadius: "8px",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <div>
                                    <div
                                      style={{
                                        fontWeight: "600",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      {sourceLabel(entry.sourceType)}
                                    </div>
                                    {entry.autoReleaseAt && (
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          fontWeight: "600",
                                          color: "#92400e",
                                        }}
                                      >
                                        ⏱️ Releases in{" "}
                                        {formatCountdown(entry.autoReleaseAt)}
                                      </div>
                                    )}
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#78350f",
                                      }}
                                    >
                                      Created:{" "}
                                      {entry.createdAt
                                        ? new Date(
                                            entry.createdAt,
                                          ).toLocaleDateString()
                                        : "—"}
                                    </div>
                                  </div>
                                  <div style={{ textAlign: "right" }}>
                                    <div
                                      style={{
                                        fontWeight: "bold",
                                        fontSize: "18px",
                                        color: "#92400e",
                                      }}
                                    >
                                      {fmt(entry.netEligibleAmount)}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#78350f",
                                      }}
                                    >
                                      Gross {fmt(entry.grossAmount)}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Released */}
                    <div
                      style={{
                        background: "white",
                        borderRadius: "12px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                        marginBottom: "16px",
                      }}
                    >
                      <div
                        onClick={() => togglePayout("released")}
                        style={{
                          padding: "16px 20px",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderBottom: payoutExpandedSections.released
                            ? "1px solid #e5e7eb"
                            : "none",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <span style={{ fontSize: "20px" }}>📤</span>
                          <div>
                            <div
                              style={{ fontWeight: "600", fontSize: "16px" }}
                            >
                              Released (Queued for Transfer)
                            </div>
                            <div style={{ fontSize: "13px", color: "#6b7280" }}>
                              {releasedEntries.length} entr
                              {releasedEntries.length !== 1
                                ? "ies"
                                : "y"} · {fmt(releasedTotal)}
                            </div>
                          </div>
                        </div>
                        <span style={{ fontSize: "18px", color: "#6b7280" }}>
                          {payoutExpandedSections.released ? "▼" : "▶"}
                        </span>
                      </div>
                      {payoutExpandedSections.released && (
                        <div style={{ padding: "20px" }}>
                          {releasedEntries.length === 0 ? (
                            <p
                              style={{
                                color: "#9ca3af",
                                textAlign: "center",
                              }}
                            >
                              No earnings currently queued for transfer
                            </p>
                          ) : (
                            <>
                              {rec.nextPayoutDate && (
                                <div
                                  style={{
                                    padding: "12px",
                                    background: "#dbeafe",
                                    borderRadius: "8px",
                                    marginBottom: "16px",
                                    fontSize: "14px",
                                    color: "#1e40af",
                                  }}
                                >
                                    💡 These earnings will be included in your next weekly payout on{" "}
                                    <strong>
                                      {`${new Date(rec.nextPayoutDate).toLocaleDateString("en-US", {
                                        month: "2-digit",
                                        day: "2-digit",
                                        year: "numeric",
                                      })} at 2:00 AM CT`}
                                    </strong>
                                </div>
                              )}
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "12px",
                                }}
                              >
                                {releasedEntries.map((entry) => (
                                  <div
                                    key={entry._id}
                                    style={{
                                      padding: "16px",
                                      background: "#f0f9ff",
                                      border: "2px solid #3b82f6",
                                      borderRadius: "8px",
                                      display: "flex",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <div>
                                      <div
                                        style={{
                                          fontWeight: "600",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        ✅ {sourceLabel(entry.sourceType)}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#6b7280",
                                        }}
                                      >
                                        Released:{" "}
                                        {entry.payoutReleasedAt
                                          ? new Date(
                                              entry.payoutReleasedAt,
                                            ).toLocaleString()
                                          : "—"}
                                      </div>
                                    </div>
                                    <div style={{ textAlign: "right" }}>
                                      <div
                                        style={{
                                          fontWeight: "bold",
                                          fontSize: "18px",
                                          color: "#1e40af",
                                        }}
                                      >
                                        {fmt(entry.netEligibleAmount)}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                        }}
                                      >
                                        Gross {fmt(entry.grossAmount)}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* History */}
                    <div
                      style={{
                        background: "white",
                        borderRadius: "12px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      }}
                    >
                      <div
                        onClick={() => togglePayout("history")}
                        style={{
                          padding: "16px 20px",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderBottom: payoutExpandedSections.history
                            ? "1px solid #e5e7eb"
                            : "none",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <span style={{ fontSize: "20px" }}>📜</span>
                          <div>
                            <div
                              style={{ fontWeight: "600", fontSize: "16px" }}
                            >
                              Payout History
                            </div>
                            <div style={{ fontSize: "13px", color: "#6b7280" }}>
                              {history.length} completed payout
                              {history.length !== 1 ? "s" : ""}
                            </div>
                          </div>
                        </div>
                        <span style={{ fontSize: "18px", color: "#6b7280" }}>
                          {payoutExpandedSections.history ? "▼" : "▶"}
                        </span>
                      </div>
                      {payoutExpandedSections.history && (
                        <div style={{ padding: "20px" }}>
                          {history.length === 0 ? (
                            <p
                              style={{
                                color: "#9ca3af",
                                textAlign: "center",
                              }}
                            >
                              No payout history yet
                            </p>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px",
                              }}
                            >
                              {history.map((h, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    padding: "16px",
                                    background: "#f9fafb",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "8px",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <div>
                                    <div
                                      style={{
                                        fontWeight: "600",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      {new Date(
                                        h.payoutDate,
                                      ).toLocaleDateString("en-US", {
                                        weekday: "short",
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "13px",
                                        color: "#6b7280",
                                      }}
                                    >
                                      {h.entryCount} earning
                                      {h.entryCount !== 1 ? "s" : ""} settled
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      fontWeight: "bold",
                                      fontSize: "20px",
                                      color: "#059669",
                                    }}
                                  >
                                    {fmt(h.amount)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()
            ) : (
              <div
                style={{
                  background: "white",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  border: "1px solid #e5e7eb",
                  padding: "24px",
                  color: "#6b7280",
                }}
              >
                Payout reconciliation is currently unavailable. Try refreshing this tab.
              </div>
            )}
          </>
        )}
      </div>
    </SidebarLayout>
  );
};

export default Analytics;
